'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useMemo, useCallback, useEffect, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { fromMoment, now } from 'UIComponents/core/SimpleDate';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UISelect from 'UIComponents/input/UISelect';
import { calculateMomentTimeRange } from 'AdsUI/lib/CalculateTimeRange';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIMicroDateInput from 'UIComponents/dates/UIMicroDateInput';
import { getDateRangePresetForString, PRESET_TYPES, AdsSimpleDateRange } from '../lib/AdsSimpleDateRange';
const HIDE_RANGE = [PRESET_TYPES.ALL, PRESET_TYPES.CUSTOM];
const SINGLE_DAY = [PRESET_TYPES.THIS_DAY, PRESET_TYPES.LAST_DAY];

const UIFormattedDateTime = ({
  value
}) => /*#__PURE__*/_jsx(FormattedDateTime, {
  value: value,
  format: "l",
  type: "userTz"
});

const TimeRangeValue = ({
  option: {
    value
  }
}) => /*#__PURE__*/_jsx(FormattedMessage, {
  message: `rangeTypes.${value}`
});

const TimeRangeFilter = ({
  onChange,
  showAllTime,
  timeRangeRecord,
  placement
}) => {
  const [dateRanges, setDateRanges] = useState({});
  useEffect(() => {
    const _dateRanges = Object.keys(PRESET_TYPES).reduce((memo, type) => {
      const dateRange = Object.assign({}, memo, {
        [type]: calculateMomentTimeRange(getDateRangePresetForString(type))
      });
      return dateRange;
    }, {});

    setDateRanges(_dateRanges);
  }, []);
  const TimeRangeOption = useCallback((_ref) => {
    let {
      option: {
        value
      }
    } = _ref,
        props = _objectWithoutPropertiesLoose(_ref, ["option"]);

    return /*#__PURE__*/_jsx("span", Object.assign({}, props, {
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "unstyled",
        title: I18n.text(`rangeTypes.${value}`),
        children: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: `rangeTypes.${value}`
          }), !HIDE_RANGE.includes(value) && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
              use: "help",
              children: SINGLE_DAY.includes(value) ? /*#__PURE__*/_jsx(FormattedDateTime, {
                value: dateRanges[value].startDate.valueOf(),
                format: "l",
                type: "userTz"
              }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "timeRange_jsx",
                options: {
                  start: dateRanges[value].startDate.valueOf(),
                  end: dateRanges[value].endDate.valueOf()
                },
                elements: {
                  UIFormattedDateTime
                }
              })
            })]
          })]
        })
      })
    }));
  }, [dateRanges]);
  const rangeOptions = useMemo(() => Object.keys(PRESET_TYPES).filter(range => showAllTime || range !== PRESET_TYPES.ALL).map(rangeType => ({
    text: rangeType,
    value: rangeType
  })), [showAllTime]);
  const handleChange = useCallback(({
    target: {
      value
    }
  }) => onChange({
    target: {
      value: value === PRESET_TYPES.CUSTOM ? getDateRangePresetForString(PRESET_TYPES.CUSTOM, fromMoment(I18n.moment()), fromMoment(I18n.moment())) : getDateRangePresetForString(value)
    }
  }), [onChange]);
  const handleCustomChange = useCallback(({
    startDate,
    endDate
  }) => onChange({
    target: {
      value: getDateRangePresetForString(PRESET_TYPES.CUSTOM, startDate, endDate)
    }
  }), [onChange]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UISelect, {
      buttonUse: "link",
      defaultValue: timeRangeRecord.type,
      optionComponent: TimeRangeOption,
      valueComponent: TimeRangeValue,
      menuWidth: 300,
      onChange: handleChange,
      options: rangeOptions,
      searchable: false,
      id: "range-type-select",
      placement: placement
    }), timeRangeRecord.type === PRESET_TYPES.CUSTOM && /*#__PURE__*/_jsxs("div", {
      className: "display-inline-block m-x-3",
      children: [/*#__PURE__*/_jsx("div", {
        className: "display-inline-block",
        children: /*#__PURE__*/_jsx(UIMicroDateInput, {
          value: timeRangeRecord.startDate,
          use: "on-dark",
          size: "small",
          max: timeRangeRecord.endDate,
          onChange: ({
            target: {
              value
            }
          }) => handleCustomChange({
            startDate: value,
            endDate: timeRangeRecord.endDate
          })
        })
      }), /*#__PURE__*/_jsx(UIIcon, {
        name: "next",
        size: 9,
        className: "m-x-2"
      }), /*#__PURE__*/_jsx("div", {
        className: "display-inline-block",
        children: /*#__PURE__*/_jsx(UIMicroDateInput, {
          value: timeRangeRecord.endDate,
          use: "on-dark",
          size: "small",
          min: timeRangeRecord.startDate,
          max: now(),
          onChange: ({
            target: {
              value
            }
          }) => handleCustomChange({
            startDate: timeRangeRecord.startDate,
            endDate: value
          })
        })
      })]
    })]
  });
};

TimeRangeFilter.propTypes = {
  showAllTime: PropTypes.bool,
  timeRangeRecord: PropTypes.instanceOf(AdsSimpleDateRange).isRequired,
  onChange: PropTypes.func.isRequired,
  placement: PropTypes.string
};
export default TimeRangeFilter;